/* eslint-disable @typescript-eslint/no-explicit-any */
import { get as amplifyGet, post as amplifyPost } from 'aws-amplify/api';
import { logger } from '../../../utils/logger';
import { trackApiCall, isMetricsInitialized } from '../../../utils/metrics';
import { dateUtils } from 'src/utils/dateUtils';

/**
 * Simple interface for REST request options
 */
interface RestGetOptions {
  apiName: string;
  path: string;
  queryParams?: Record<string, string | number | boolean>;
  headers?: Record<string, string>;
}

/**
 * Simple interface for REST POST request options
 */
interface RestPostOptions {
  apiName: string;
  path: string;
  body?: any;
  headers?: Record<string, string>;
}

/**
 * Execute a REST GET request with basic error handling and logging
 * This is a simplified wrapper around Amplify's REST API client
 *
 * @param options - REST request options
 * @returns Promise resolving to typed response data
 */
export const get = async <T>(options: RestGetOptions): Promise<T> => {
  const startTime = dateUtils.now();
  const { apiName, path, queryParams, headers } = options;

  try {
    // Convert query parameters to strings as required by Amplify v6
    const stringQueryParams = queryParams
      ? Object.entries(queryParams).reduce<Record<string, string>>((acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        }, {})
      : undefined;

    // Execute the REST GET request using Amplify v6 API
    const restOperation = amplifyGet({
      apiName,
      path,
      options: {
        queryParams: stringQueryParams,
        headers,
      },
    });

    // Wait for the response
    const response = await restOperation.response;

    // Parse the response body as JSON
    const responseBody = await response.body.json();

    // Calculate response time for logging
    const elapsed = Date.now() - startTime.getTime();

    // Log successful API call
    logger.info(`API_SUCCESS: GET ${apiName}${path} completed in ${elapsed}ms`);

    // Track API call metrics (only if metrics are initialized)
    if (isMetricsInitialized()) {
      trackApiCall(`${apiName}${path}`, 'GET', elapsed, true);
    }

    // Return the parsed response body as the expected type
    return responseBody as T;
  } catch (error: unknown) {
    // Calculate response time for logging
    const elapsed = Date.now() - startTime.getTime();

    // Format error message
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

    // Log the error
    logger.error(`API_FAILED: GET ${apiName}${path} failed in ${elapsed}ms`, {
      error: errorMessage,
      originalError: error,
    });

    // Track failed API call metrics (only if metrics are initialized)
    if (isMetricsInitialized()) {
      trackApiCall(`${apiName}${path}`, 'GET', elapsed, false);
    }

    // Re-throw the error for the service to handle
    throw error;
  }
};

/**
 * Execute a REST POST request with basic error handling and logging
 * This is a simplified wrapper around Amplify's REST API client
 *
 * @param options - REST POST request options
 * @returns Promise resolving to typed response data
 */
export const post = async <T>(options: RestPostOptions): Promise<T> => {
  const startTime = new Date();
  const { apiName, path, body, headers } = options;

  try {
    // Execute the REST POST request using Amplify v6 API
    const restOperation = amplifyPost({
      apiName,
      path,
      options: {
        body,
        headers,
      },
    });

    // Wait for the response
    const response = await restOperation.response;

    // Parse the response body as JSON
    const responseBody = await response.body.json();

    // Calculate response time for logging
    const elapsed = Date.now() - startTime.getTime();

    // Log successful API call
    logger.info(`API_SUCCESS: POST ${apiName}${path} completed in ${elapsed}ms`);

    // Track API call metrics (only if metrics are initialized)
    if (isMetricsInitialized()) {
      trackApiCall(`${apiName}${path}`, 'POST', elapsed, true);
    }

    // Return the parsed response body as the expected type
    return responseBody as T;
  } catch (error: unknown) {
    // Calculate response time for logging
    const elapsed = Date.now() - startTime.getTime();

    // Format error message
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

    // Log the error
    logger.error(`API_FAILED: POST ${apiName}${path} failed in ${elapsed}ms`, {
      error: errorMessage,
      originalError: error,
    });

    // Track failed API call metrics (only if metrics are initialized)
    if (isMetricsInitialized()) {
      trackApiCall(`${apiName}${path}`, 'POST', elapsed, false);
    }

    // Re-throw the error for the service to handle
    throw error;
  }
};
